import { Button, Dialog, Paragraph } from "@citydna/common"
import { Box } from "@material-ui/core"
import { useSnackbar } from "notistack"
import React, { FunctionComponent, useCallback, useState } from "react"
import { DragDropArea } from "../common/DragDropArea"
import { Schema } from "./schema"
import { saveAs } from "file-saver"
import { SystemUpdateAlt as SystemUpdateAltIcon } from "@material-ui/icons"

/** ImportExportSchemaButton component props */
interface ImportExportSchemaButtonProps {
  /** The schema to download */
  schema?: Schema | undefined
  /** Prefix for the schema.json filename */
  schemaPrefix?: string
  /** On import, do this with the new schema */
  onConfirmImport: (importedSchema: Schema) => void
}

export const ImportExportSchemaButton: FunctionComponent<ImportExportSchemaButtonProps> = ({
  schemaPrefix,
  schema,
  onConfirmImport,
}) => {
  const [open, setOpen] = useState(false)
  const [importSchema, setImportSchema] = useState<Schema | undefined>()
  const { enqueueSnackbar } = useSnackbar()
  const onDrop = useCallback(
    ([file]) => {
      new Promise<Schema>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e: ProgressEvent<FileReader>) =>
          e.target?.result
            ? resolve(JSON.parse(e.target.result as string))
            : reject("Invalid JSON")
        reader.onerror = reject
        reader.readAsText(file)
      })
        .then(res => setImportSchema(res))
        .catch(err =>
          enqueueSnackbar("Error uploading JSON Schema " + err, {
            variant: "error",
          })
        )
    },
    [enqueueSnackbar]
  )

  /** Handle dialog close */
  const handleClose = () => {
    setOpen(false)
    setImportSchema(undefined)
  }

  /** Handle cancel field upload */
  const handleCancel = () => {
    setImportSchema(undefined)
  }

  /** Handle importing the fields */
  const handleImport = () => {
    importSchema && onConfirmImport(importSchema)
    setImportSchema(undefined)
    setOpen(false)
  }

  /** Handle the schema being downloaded */
  const handleExport = () => {
    if (schema) {
      saveAs(
        new Blob([JSON.stringify(schema, null, 2)], {
          type: "application/json",
        }),
        `${schemaPrefix ? schemaPrefix + "-" : ""}schema.json`
      )
    } else {
      enqueueSnackbar("No schema passed")
    }
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>Import/export schema</Button>
      <Dialog
        open={open}
        fullScreen={false}
        onClose={handleClose}
        title="Import/export schema"
        actions={
          <Box width="100%" display="flex" pl={2} pr={2} pb={1}>
            <Button
              variant="contained"
              color="secondary"
              disabled={!importSchema}
              onClick={handleImport}
            >
              Import
              {importSchema ? " " + importSchema.fields.length + " " : " "}
              fields
            </Button>
            {importSchema && (
              <Button
                style={{ marginLeft: "auto" }}
                onClick={handleCancel}
                color="primary"
              >
                Cancel
              </Button>
            )}
            {!importSchema && (
              <Button
                startIcon={<SystemUpdateAltIcon />}
                style={{ marginLeft: "auto" }}
                onClick={handleExport}
              >
                Export schema
              </Button>
            )}
          </Box>
        }
      >
        <Paragraph style={{ marginBottom: 16 }}>
          You can download the schema for this feature layer, or import an
          exported schema from another feature layer to apply it to this one.
        </Paragraph>
        {!importSchema && (
          <DragDropArea
            dragActiveText="Drop schema"
            dragInactiveText="Drag and drop schema.json here to import"
            multiple={false}
            accept="application/json"
            onDrop={onDrop}
          />
        )}
      </Dialog>
    </>
  )
}
